import React, { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'

import { EuiButton, EuiCheckbox, EuiFieldNumber, EuiFlexGroup, EuiFlexItem, EuiFlyoutBody, EuiFlyoutFooter, EuiFlyoutHeader, EuiFormRow, EuiTitle } from '@elastic/eui'
import AcFlyout from 'components/AcFlyout'

import { useGetAIPlanStubQuery, useEditAIPlanStubCampaignBuilderMutation } from 'api/rtkQueryApi/platform/aiPlansApi'
import { AcEuiFormRow } from 'components/Forms/AcEuiFormRow'

export interface EditAIPlanCampaignProjectionsFlyoutProps {
  campaignBuilderId: string
  onClose: () => void
}

const predefinedOffAdCritterPlatforms = ['Facebook', 'Google', 'Instagram', 'LinkedIn', 'TikTok']

const EditAIPlanCampaignProjectionsFlyout: React.FC<EditAIPlanCampaignProjectionsFlyoutProps> = ({ campaignBuilderId, onClose }) => {
  const { aiPlanId } = useParams<{ aiPlanId: string }>()
  const { data: stub } = useGetAIPlanStubQuery({ aiPlanId }, { skip: !aiPlanId })
  const didFireUpdate = useRef(false)

  const builder = stub?.campaignBuilders.find(b => b.id === campaignBuilderId)

  const isCustomOffAdCritter = builder?.isOffAdCritter && !predefinedOffAdCritterPlatforms.includes(builder.offAdCritterName)
  const isGoogleCampaign = builder?.isOffAdCritter && builder?.offAdCritterName === 'Google'

  const [includeInProjections, setIncludeInProjections] = useState(builder?.includeInProjections || false)
  const [includeInGeoHeatmap, setIncludeInGeoHeatmap] = useState(builder?.includeInGeoHeatmap || false)
  const [marginOfError, setMarginOfError] = useState<number | undefined>(builder?.marginOfError ? builder.marginOfError * 100 : undefined)
  const [cpm, setCpm] = useState<number | undefined>(builder?.cpm != null ? Math.round(builder.cpm * 100) / 100 : undefined)
  const [cpc, setCpc] = useState<number | undefined>(builder?.cpc != null ? Math.round(builder.cpc * 100) / 100 : undefined)

  const [editCampaignBuilder, { isSuccess, isLoading }] = useEditAIPlanStubCampaignBuilderMutation()

  useEffect(() => {
    if (didFireUpdate.current && isSuccess) {
      didFireUpdate.current = false
      onClose()
    }
  }, [didFireUpdate, isSuccess, onClose])

  if (!builder || !stub) return null

  const hasZips = (builder.zips?.length ?? 0) > 0

  const onSave = () => {
    const request = {
      name: builder.name,
      shortSummary: builder.shortSummary,
      longSummary: builder.longSummary,
      audienceSummary: builder.audienceSummary,
      audienceDetails: builder.audienceDetails || '',
      geoSummary: builder.geoSummary,
      geoDetails: builder.geoDetails || '',
      seasonalitySummary: builder.seasonalitySummary,
      seasonalityDetails: builder.seasonalityDetails || '',
      campaignType: builder.campaignType as unknown as string,
      isOffAdCritter: builder.isOffAdCritter,
      offAdCritterName: builder.offAdCritterName,
      offAdCritterColor: builder.offAdCritterColor,
      intervalBudget: builder.intervalBudget,
      retargetSite: builder.retargetSite,
      isTotalBudget: builder.isTotalBudget,
      startDate: builder.startDate as unknown as string,
      endDate: builder.endDate as unknown as string | undefined,
      intervalUnit: builder.intervalUnit as unknown as string,
      intervalFrequency: builder.intervalFrequency,
      intervalDuration: builder.intervalDuration,
      segmentTargets: builder.segmentTargets || [],
      audienceSegmentLooksUp: builder.audienceSegmentLooksUp || [],
      targetingType: builder.targetingType as unknown as string | undefined,
      regions: builder.regions || [],
      dmas: builder.dmas || [],
      cities: builder.cities || [],
      zips: builder.zips || [],
      keywords: builder.keywords || [],
      keywordDescription: builder.keywordDescription || '',
      segmentDescriptions: builder.segmentDescriptions || [],
      includeInProjections,
      includeInGeoHeatmap: includeInProjections ? includeInGeoHeatmap : false,
      cpm,
      cpc,
      marginOfError: isCustomOffAdCritter && marginOfError ? marginOfError / 100 : builder.marginOfError
    }

    editCampaignBuilder({
      aiPlanStubId: stub.id,
      campaignBuilderId,
      aiPlanId,
      request
    })
    didFireUpdate.current = true
  }

  return (
    <AcFlyout onClose={onClose} size='s'>
      <EuiFlyoutHeader>
        <EuiTitle size='m'>
          <h2>Projection Settings</h2>
        </EuiTitle>
      </EuiFlyoutHeader>
      <EuiFlyoutBody>
        <EuiFormRow fullWidth>
          <EuiCheckbox
            id='projections-includeInProjections'
            label='Include in Projections'
            checked={includeInProjections}
            onChange={e => {
              const checked = e.target.checked
              setIncludeInProjections(checked)
              if (!checked) {
                setIncludeInGeoHeatmap(false)
              }
            }}
          />
        </EuiFormRow>

        {includeInProjections && (
          <>
            {isGoogleCampaign ? (
              <AcEuiFormRow fullWidth label='CPC Estimate'>
                <EuiFieldNumber prepend='$' value={cpc ?? ''} onChange={e => setCpc(e.target.value ? Math.round(Number(e.target.value) * 100) / 100 : undefined)} step={0.01} />
              </AcEuiFormRow>
            ) : (
              <AcEuiFormRow fullWidth label='CPM Estimate'>
                <EuiFieldNumber prepend='$' value={cpm ?? ''} onChange={e => setCpm(e.target.value ? Math.round(Number(e.target.value) * 100) / 100 : undefined)} step={0.01} />
              </AcEuiFormRow>
            )}

            {isCustomOffAdCritter && (
              <AcEuiFormRow fullWidth label='Margin of Error'>
                <EuiFieldNumber append='%' value={marginOfError ?? ''} onChange={e => setMarginOfError(e.target.value ? Number(e.target.value) : undefined)} />
              </AcEuiFormRow>
            )}
          </>
        )}

        <EuiFormRow fullWidth>
          <EuiCheckbox
            id='projections-includeInGeoHeatmap'
            label='Add to Geo Distribution heatmap. (Requires zip codes.)'
            checked={includeInGeoHeatmap}
            onChange={e => setIncludeInGeoHeatmap(e.target.checked)}
            disabled={!includeInProjections || !hasZips}
          />
        </EuiFormRow>
      </EuiFlyoutBody>
      <EuiFlyoutFooter>
        <EuiFlexGroup direction='row' alignItems='center'>
          <EuiFlexItem grow={false}>
            <EuiButton onClick={onClose}>Cancel</EuiButton>
          </EuiFlexItem>
          <EuiFlexItem grow={false}>
            <EuiButton fill onClick={onSave} isLoading={isLoading}>
              Save
            </EuiButton>
          </EuiFlexItem>
        </EuiFlexGroup>
      </EuiFlyoutFooter>
    </AcFlyout>
  )
}

export default EditAIPlanCampaignProjectionsFlyout
